import React, { useCallback, useMemo, useState } from 'react'
import { HiChevronDown, HiChevronRight } from 'react-icons/hi'

const collectIds = (nodes) => {
  const ids = []
  const walk = (list) => {
    for (const node of list) {
      ids.push(node.id)
      if (node.children?.length) walk(node.children)
    }
  }
  walk(nodes)
  return ids
}

const collectDescendantIds = (node) => {
  const ids = []
  const walk = (list) => {
    for (const child of list) {
      ids.push(child.id)
      if (child.children?.length) walk(child.children)
    }
  }
  if (node.children?.length) walk(node.children)
  return ids
}

const indexNodes = (nodes, parentId = null, map = new Map()) => {
  for (const node of nodes) {
    map.set(node.id, {
      node,
      parentId,
      childIds: (node.children || []).map((c) => c.id),
      descendantIds: collectDescendantIds(node),
    })
    if (node.children?.length) {
      indexNodes(node.children, node.id, map)
    }
  }
  return map
}

const CheckboxTreeView = ({
  nodes = [],
  defaultChecked = [],
  checked: checkedProp,
  onCheckedChange,
  defaultExpanded = [],
  expanded: expandedProp,
  onExpandedChange,
  className = '',
}) => {
  const nodeIndex = useMemo(() => indexNodes(nodes), [nodes])
  const allIds = useMemo(() => collectIds(nodes), [nodes])

  const [internalChecked, setInternalChecked] = useState(() => new Set(defaultChecked))
  const [internalExpanded, setInternalExpanded] = useState(() => new Set(defaultExpanded))

  const isCheckedControlled = checkedProp !== undefined
  const isExpandedControlled = expandedProp !== undefined

  const checkedSet = useMemo(
    () => (isCheckedControlled ? new Set(checkedProp) : internalChecked),
    [isCheckedControlled, checkedProp, internalChecked],
  )

  const expandedSet = useMemo(
    () => (isExpandedControlled ? new Set(expandedProp) : internalExpanded),
    [isExpandedControlled, expandedProp, internalExpanded],
  )

  const emitChecked = useCallback(
    (next) => {
      if (!isCheckedControlled) setInternalChecked(next)
      onCheckedChange?.(Array.from(next))
    },
    [isCheckedControlled, onCheckedChange],
  )

  const emitExpanded = useCallback(
    (next) => {
      if (!isExpandedControlled) setInternalExpanded(next)
      onExpandedChange?.(Array.from(next))
    },
    [isExpandedControlled, onExpandedChange],
  )

  const getCheckState = useCallback(
    (id) => {
      const meta = nodeIndex.get(id)
      if (!meta?.descendantIds.length) {
        return { checked: checkedSet.has(id), indeterminate: false }
      }

      const selectedCount = meta.descendantIds.filter((d) => checkedSet.has(d)).length
      if (selectedCount === 0) {
        return { checked: checkedSet.has(id), indeterminate: false }
      }
      if (selectedCount === meta.descendantIds.length) {
        return { checked: true, indeterminate: false }
      }
      return { checked: false, indeterminate: true }
    },
    [checkedSet, nodeIndex],
  )

  const toggleChecked = useCallback(
    (id) => {
      const meta = nodeIndex.get(id)
      if (!meta) return

      const next = new Set(checkedSet)
      const { checked, indeterminate } = getCheckState(id)
      const shouldCheck = !(checked || indeterminate)

      const targets = [id, ...meta.descendantIds]
      for (const targetId of targets) {
        if (shouldCheck) next.add(targetId)
        else next.delete(targetId)
      }

      // Bubble parent states up from this node
      let parentId = meta.parentId
      while (parentId) {
        const parentMeta = nodeIndex.get(parentId)
        if (!parentMeta) break
        const selectedCount = parentMeta.descendantIds.filter((d) => next.has(d)).length
        if (selectedCount === parentMeta.descendantIds.length) next.add(parentId)
        else next.delete(parentId)
        parentId = parentMeta.parentId
      }

      emitChecked(next)
    },
    [checkedSet, emitChecked, getCheckState, nodeIndex],
  )

  const toggleExpanded = useCallback(
    (id) => {
      const next = new Set(expandedSet)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      emitExpanded(next)
    },
    [emitExpanded, expandedSet],
  )

  const renderNodes = (list, depth = 0) =>
    list.map((node) => {
      const hasChildren = Boolean(node.children?.length)
      const isExpanded = expandedSet.has(node.id)
      const { checked, indeterminate } = getCheckState(node.id)

      return (
        <li key={node.id} className="list-none">
          <div
            className="flex items-start gap-2 py-1.5 min-w-0"
            style={{ paddingLeft: depth * 1.25 + 'rem' }}
          >
            {hasChildren ? (
              <button
                type="button"
                onClick={() => toggleExpanded(node.id)}
                className="mt-0.5 shrink-0 p-0.5 rounded text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                aria-expanded={isExpanded}
                aria-label={isExpanded ? `Collapse ${node.id}` : `Expand ${node.id}`}
              >
                {isExpanded ? (
                  <HiChevronDown className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <HiChevronRight className="h-4 w-4" aria-hidden="true" />
                )}
              </button>
            ) : (
              <span className="mt-0.5 inline-block w-5 shrink-0" aria-hidden="true" />
            )}

            <input
              type="checkbox"
              className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800"
              checked={checked}
              ref={(el) => {
                if (el) el.indeterminate = indeterminate
              }}
              onChange={() => toggleChecked(node.id)}
              disabled={node.disabled}
              aria-label={typeof node.label === 'string' ? node.label : `Select ${node.id}`}
            />

            <div className="min-w-0 flex-1 text-sm text-gray-900 dark:text-gray-100">
              {node.label}
            </div>
          </div>

          {hasChildren && isExpanded && (
            <ul className="m-0 p-0">{renderNodes(node.children, depth + 1)}</ul>
          )}
        </li>
      )
    })

  if (!nodes.length) return null

  return (
    <ul
      className={`m-0 p-0 ${className}`}
      role="tree"
      data-node-count={allIds.length}
    >
      {renderNodes(nodes)}
    </ul>
  )
}

export default CheckboxTreeView
